import { Injectable } from '@angular/core';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { monthlyTag } from '../models/tag';

export interface DateRange {
  firstDay: Date;
  lastDay: Date;
}

@Injectable({
  providedIn: 'root'
})
export class CalendarService {

  /**
   * Gera as datas de início e fim para um mês específico
   */
  getMonthDateRange(monthOffset: number = 0): DateRange {
    const date = new Date();
    const targetMonth = date.getMonth() + monthOffset;
    const targetYear = date.getFullYear();
    
    const firstDay = new Date(targetYear, targetMonth, 1);
    const lastDay = new Date(targetYear, targetMonth + 1, 0);
    
    return { firstDay, lastDay };
  }

  /**
   * Gera múltiplos ranges de datas para diferentes meses
   */
  getMultipleMonthRanges(months: number[]): Map<number, DateRange> {
    const ranges = new Map<number, DateRange>();
    
    months.forEach(monthOffset => {
      ranges.set(monthOffset, this.getMonthDateRange(monthOffset));
    });
    
    return ranges;
  }

  /**
   * Cria uma função de classificação para o calendário baseada nas tags
   */
  createDateClassFunction(tags: monthlyTag[], days: number[]): MatCalendarCellClassFunction<Date> {
    return (cellDate, _) => {
      // Destacar o dia atual
      if (this.isToday(cellDate)) {
        return 'today-highlight';
      }
      
      for (let i = 0; i < days.length; i++) {
        if (days[i] === cellDate.getDate()) {
          return this.getTagClass(tags[i]?.tag);
        }
      }
      return '';
    };
  }

  /**
   * Verifica se a data é hoje
   */
  private isToday(cellDate: Date): boolean {
    const today = new Date();
    return cellDate.getDate() === today.getDate() &&
           cellDate.getMonth() === today.getMonth() &&
           cellDate.getFullYear() === today.getFullYear();
  }

  /**
   * Mapeia o nome da tag para a classe CSS
   */
  private getTagClass(tagName: string): string {
    const tagClassMap: { [key: string]: string } = {
      'Green': 'green-background',
      'Red': 'red-background',
      'Blue': 'blue-background',
      'Orange': 'orange-background',
      'Yellow': 'yellow-background',
      'Purple': 'purple-background',
      'Gray': 'gray-background',
      'Green-Light': 'green-light-background'
    };
    
    return tagClassMap[tagName] || 'green-background';
  }

  /**
   * Processa tags de um mês e extrai os dias
   */
  processMonthTags(tags: monthlyTag[]): number[] {
    const days: number[] = [];
    
    if (tags) {
      tags.forEach(month => {
        const newDate = new Date(month.date);
        newDate.setDate(newDate.getDate() + 1);
        days.push(newDate.getDate());
      });
    }
    
    return days;
  }

  /**
   * Cria um range de datas para um dia específico
   */
  createDayDateRange(date: Date): { start: Date; end: Date } {
    const start = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0, 0
    );
    
    const end = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23, 59, 59, 999
    );
    
    return { start, end };
  }

  /**
   * Verifica se uma data é futura
   */
  isFutureDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  }

  /**
   * Formata uma data para string no formato brasileiro
   */
  formatDateToBrazilian(date: Date): string {
    return date.toLocaleDateString('pt-BR');
  }
}
